import { useQuery } from "@tanstack/react-query";
import { useDirectoryUsers } from "@/hooks/useDirectoryUsers";
import { useConsoleUsers } from "@/hooks/rbac/useUsers";
import { UnifiedUser, UserType, UnifiedUsersStats } from "@/types/unified-user";
import { useMemo } from "react";
import { listDevices } from "@/services/devicesApi";

export function useUnifiedUsers() {
  const { users: directoryUsers, isLoading: isLoadingDirectory } = useDirectoryUsers();
  const { data: consoleUsers, isLoading: isLoadingConsole } = useConsoleUsers();

  const { data: devices, isLoading: isLoadingDevices } = useQuery({
    queryKey: ['devices-enrollment'],
    queryFn: async () => {
      try {
        const result = await listDevices();
        const arr = Array.isArray(result) ? result : (result as any)?.data || [];
        return arr.map((d: any) => ({
          id: d.id || d.deviceId || '',
          name: d.name || d.deviceName || '',
          status: d.status || 'unknown',
          last_seen: d.lastSeen || d.last_seen || null,
          enrollment_username: d.enrollmentUsername || d.enrollment_username || null,
        }));
      } catch { return []; }
    },
  });

  const isLoading = isLoadingDirectory || isLoadingConsole || isLoadingDevices;

  const { users, stats } = useMemo(() => {
    if (!devices || !directoryUsers || !consoleUsers) {
      return { users: [], stats: { total: 0, directory: 0, console: 0, both: 0, totalDevices: 0, activeUsers: 0 } as UnifiedUsersStats };
    }

    const devicesMap = new Map<string, typeof devices>();
    devices.forEach((device: any) => {
      if (device.enrollment_username) {
        const username = device.enrollment_username.toLowerCase();
        if (!devicesMap.has(username)) devicesMap.set(username, []);
        devicesMap.get(username)!.push(device);
      }
    });

    const consoleUsersByEmail = new Map<string, typeof consoleUsers[0]>();
    consoleUsers.forEach(user => consoleUsersByEmail.set(user.email.toLowerCase(), user));

    const unifiedUsers: UnifiedUser[] = [];
    const processedEmails = new Set<string>();

    directoryUsers.forEach(dirUser => {
      const email = dirUser.email.toLowerCase();
      const username = dirUser.username.toLowerCase();
      const userDevices = devicesMap.get(username) || [];
      const consoleUser = consoleUsersByEmail.get(email);
      processedEmails.add(email);
      const type: UserType = consoleUser ? 'both' : 'directory';
      unifiedUsers.push({
        id: dirUser.id, type, email: dirUser.email,
        name: `${dirUser.firstName} ${dirUser.lastName}`,
        status: dirUser.status === 'active' ? 'active' : 'inactive',
        lastActive: dirUser.lastLogin || dirUser.syncedAt,
        directoryId: dirUser.id, username: dirUser.username,
        firstName: dirUser.firstName, lastName: dirUser.lastName,
        department: dirUser.department, title: dirUser.title,
        provider: dirUser.provider, customer: dirUser.customer,
        groups: dirUser.groups, externalId: dirUser.externalId,
        managerEmail: dirUser.managerEmail, attributes: dirUser.attributes,
        syncedAt: dirUser.syncedAt,
        consoleId: consoleUser?.id, roles: consoleUser?.user_roles,
        joinDate: consoleUser?.created_at,
        deviceCount: userDevices.length,
        devices: userDevices.map((d: any) => ({ id: d.id, name: d.name, status: d.status || 'unknown', lastSeen: d.last_seen })),
      });
    });

    consoleUsers.forEach(consoleUser => {
      if (!processedEmails.has(consoleUser.email.toLowerCase())) {
        unifiedUsers.push({
          id: consoleUser.id, type: 'console', email: consoleUser.email,
          name: consoleUser.email.split('@')[0], status: 'active',
          lastActive: consoleUser.last_sign_in_at || consoleUser.created_at,
          consoleId: consoleUser.id, roles: consoleUser.user_roles,
          joinDate: consoleUser.created_at, deviceCount: 0, devices: [],
        });
      }
    });

    const stats: UnifiedUsersStats = {
      total: unifiedUsers.length,
      directory: unifiedUsers.filter(u => u.type === 'directory' || u.type === 'both').length,
      console: unifiedUsers.filter(u => u.type === 'console' || u.type === 'both').length,
      both: unifiedUsers.filter(u => u.type === 'both').length,
      totalDevices: devices.length,
      activeUsers: unifiedUsers.filter(u => u.status === 'active').length,
    };
    return { users: unifiedUsers, stats };
  }, [devices, directoryUsers, consoleUsers]);

  return { users, stats, isLoading };
}
